import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Loader2, RefreshCw, Mail, Phone, Save, PartyPopper, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const MISSING_STYLE = {
  both: 'bg-red-100 text-red-700',
  email: 'bg-amber-100 text-amber-800',
  phone: 'bg-blue-100 text-blue-800',
};
const MISSING_LABEL = { both: 'Email + Phone', email: 'Email', phone: 'Phone' };

export function MissingContactTab({ api, onChanged }) {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ total: 0, both: 0, email: 0, phone: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');       // all | email | phone | both
  const [q, setQ] = useState('');
  const [edits, setEdits] = useState({});            // {donorId: {email, phone}}
  const [savingId, setSavingId] = useState(null);
  const [fixedCount, setFixedCount] = useState(0);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/donor-missing-contact');
      setRows(data.donors);
      setCounts(data.counts);
      setEdits({});
    } catch { toast.error('Failed to load missing-contact list'); }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    let list = rows;
    if (filter !== 'all') list = list.filter(r => r.missing === filter);
    const s = q.trim().toLowerCase();
    if (s) list = list.filter(r => (r.name || '').toLowerCase().includes(s) || (r.org || '').toLowerCase().includes(s));
    return list;
  }, [rows, filter, q]);

  const setEdit = (id, field, value) =>
    setEdits(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));

  const saveRow = async (row) => {
    const e = edits[row.id] || {};
    const newEmail = (e.email ?? '').trim();
    const newPhone = (e.phone ?? '').trim();
    if (!newEmail && !newPhone) { toast.error('Enter an email or phone first'); return; }
    const payload = {};
    if (newEmail) {
      payload.email = newEmail;
      payload.emails = [{ email: newEmail, label: '', primary: true }];
    }
    if (newPhone) payload.phone = newPhone;
    setSavingId(row.id);
    try {
      await api.put(`/donors/${row.id}`, payload);
      const email = newEmail || row.email;
      const phone = newPhone || row.phone;
      const stillMissing = !email ? (!phone ? 'both' : 'email') : (!phone ? 'phone' : null);
      setEdits(prev => { const n = { ...prev }; delete n[row.id]; return n; });
      if (!stillMissing) {
        setRows(prev => prev.filter(r => r.id !== row.id));
        setCounts(prev => ({ ...prev, total: prev.total - 1, [row.missing]: prev[row.missing] - 1 }));
        setFixedCount(c => c + 1);
        toast.success(`${row.name || 'Donor'} is complete — removed from list`);
      } else {
        setRows(prev => prev.map(r => r.id === row.id ? { ...r, email, phone, missing: stillMissing } : r));
        setCounts(prev => ({ ...prev, [row.missing]: prev[row.missing] - 1, [stillMissing]: prev[stillMissing] + 1 }));
        toast.success(`Saved — ${row.name || 'donor'} still missing ${MISSING_LABEL[stillMissing].toLowerCase()}`);
      }
      onChanged && onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    setSavingId(null);
  };

  const hasEdit = (id) => {
    const e = edits[id] || {};
    return Boolean((e.email ?? '').trim() || (e.phone ?? '').trim());
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#6B3FA0]" /></div>;

  const pills = [
    { key: 'all', label: `All (${counts.total})` },
    { key: 'both', label: `Missing Both (${counts.both})` },
    { key: 'email', label: `Missing Email (${counts.email})` },
    { key: 'phone', label: `Missing Phone (${counts.phone})` },
  ];

  return (
    <div className="space-y-3" data-testid="missing-contact-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-sm text-gray-700 font-medium">Missing Contact Information</p>
          <p className="text-xs text-gray-500">Type the missing email/phone right on the row and hit Save — completed donors drop off the list automatically.</p>
        </div>
        <div className="flex items-center gap-2">
          {fixedCount > 0 && (
            <span className="text-xs text-emerald-700 font-medium flex items-center gap-1" data-testid="fixed-count">
              <PartyPopper className="h-3.5 w-3.5" /> {fixedCount} fixed this session
            </span>
          )}
          <Button size="sm" variant="outline" onClick={load} data-testid="missing-contact-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {pills.map(p => (
          <button key={p.key} onClick={() => setFilter(p.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${filter === p.key ? 'bg-[#6B3FA0] text-white border-[#6B3FA0]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#6B3FA0]'}`}
                  data-testid={`missing-filter-${p.key}`}>
            {p.label}
          </button>
        ))}
        <div className="relative ml-auto">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-gray-400" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name or org…"
                 className="h-8 pl-8 w-56" data-testid="missing-contact-search" />
        </div>
      </div>

      {visible.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-sm text-gray-500" data-testid="missing-contact-empty">
          {counts.total === 0 ? '🎉 Every donor has an email and phone on file. Nothing to clean up!' : 'No donors match this filter/search.'}
        </CardContent></Card>
      ) : (
        <Card><CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b bg-gray-50">
                  <th className="px-3 py-2 font-medium">Donor</th>
                  <th className="px-3 py-2 font-medium">Missing</th>
                  <th className="px-3 py-2 font-medium">Email</th>
                  <th className="px-3 py-2 font-medium">Phone</th>
                  <th className="px-3 py-2 font-medium text-right">Lifetime</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {visible.map(row => {
                  const e = edits[row.id] || {};
                  return (
                    <tr key={row.id} className="hover:bg-gray-50" data-testid={`missing-row-${row.id}`}>
                      <td className="px-3 py-2">
                        <button className="font-medium hover:underline text-left" onClick={() => navigate(`/donors/${row.id}`)}
                                data-testid={`missing-name-${row.id}`}>
                          {row.name || '(unnamed)'}
                        </button>
                        {row.org && <div className="text-xs text-gray-400">{row.org}</div>}
                      </td>
                      <td className="px-3 py-2">
                        <Badge className={`${MISSING_STYLE[row.missing]} border-0 text-[11px]`} data-testid={`missing-badge-${row.id}`}>
                          {MISSING_LABEL[row.missing]}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 min-w-[220px]">
                        {row.email ? (
                          <span className="flex items-center gap-1.5 text-gray-700"><Mail className="h-3.5 w-3.5 text-gray-400" />{row.email}</span>
                        ) : (
                          <Input type="email" value={e.email ?? ''} placeholder="Add email…"
                                 onChange={ev => setEdit(row.id, 'email', ev.target.value)}
                                 onKeyDown={ev => ev.key === 'Enter' && hasEdit(row.id) && saveRow(row)}
                                 className="h-8" data-testid={`missing-email-input-${row.id}`} />
                        )}
                      </td>
                      <td className="px-3 py-2 min-w-[170px]">
                        {row.phone ? (
                          <span className="flex items-center gap-1.5 text-gray-700"><Phone className="h-3.5 w-3.5 text-gray-400" />{row.phone}</span>
                        ) : (
                          <Input type="tel" value={e.phone ?? ''} placeholder="Add phone…"
                                 onChange={ev => setEdit(row.id, 'phone', ev.target.value)}
                                 onKeyDown={ev => ev.key === 'Enter' && hasEdit(row.id) && saveRow(row)}
                                 className="h-8" data-testid={`missing-phone-input-${row.id}`} />
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">{fmt$(row.total_given)}</td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" disabled={!hasEdit(row.id) || savingId === row.id} onClick={() => saveRow(row)}
                                className="h-8" data-testid={`missing-save-${row.id}`}>
                          {savingId === row.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-1" /> Save</>}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent></Card>
      )}
      <p className="text-[11px] text-gray-400">Tip: press Enter inside a field to save that row instantly.</p>
    </div>
  );
}
